import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Share2 } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { WHATSAPP_NUMBER, INSTAGRAM_URL, FACEBOOK_URL, EMAIL } from "../content/siteContent";
import { IconWhatsApp, IconInstagram, IconFacebook, IconMail, IconClose } from "./Icons";

const itemClass =
    "group relative flex h-11 w-11 items-center justify-center rounded-full border border-ink/10 bg-surface/90 text-pine shadow-[0_10px_25px_-12px_rgba(0,77,77,0.4)] backdrop-blur-xl transition-[background-color,color,transform,border-color] duration-300 hover:-translate-y-1 hover:border-gold-500 hover:bg-pine hover:text-cream active:scale-90";

const SocialBar = () => {
    const { t } = useLanguage();
    const reduce = useReducedMotion();
    const [open, setOpen] = useState(false);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        if (!open) return undefined;
        const timer = setTimeout(() => setOpen(false), 5000);
        return () => clearTimeout(timer);
    }, [open, tick]);
    const links = [
        { id: "whatsapp", href: `https://wa.me/${WHATSAPP_NUMBER}`, label: t.contact.whatsappLabel, Icon: IconWhatsApp, external: true },
        { id: "instagram", href: INSTAGRAM_URL, label: t.contact.instagramLabel, Icon: IconInstagram, external: true },
        { id: "facebook", href: FACEBOOK_URL, label: t.contact.facebookLabel, Icon: IconFacebook, external: true },
        { id: "email", href: `mailto:${EMAIL}`, label: t.contact.emailLabel, Icon: IconMail, external: false },
    ];

    const renderLink = ({ id, href, label, Icon, external }, i = null) => {
        const link = (
            <a
                href={href}
                target={external ? "_blank" : undefined}
                rel={external ? "noopener noreferrer" : undefined}
                aria-label={label}
                data-testid={`social-${id}`}
                onClick={i !== null ? () => setOpen(false) : undefined}
                className={itemClass}
            >
                <Icon className="h-5 w-5" />
                <span
                    aria-hidden="true"
                    data-testid={`social-tooltip-${id}`}
                    className="pointer-events-none absolute end-[calc(100%+10px)] top-1/2 -translate-y-1/2 scale-95 whitespace-nowrap rounded-full bg-pine px-3 py-1.5 text-xs font-semibold text-cream opacity-0 shadow-lg transition-[opacity,transform] duration-200 group-hover:scale-100 group-hover:opacity-100 group-focus-visible:scale-100 group-focus-visible:opacity-100"
                >
                    {label}
                </span>
            </a>
        );
        if (i === null) return <div key={id}>{link}</div>;
        if (reduce) return <div key={id}>{link}</div>;
        return (
            <motion.div
                key={id}
                style={{ transformOrigin: "bottom center" }}
                initial={{ opacity: 0, scale: 0.3, y: 18 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.3, y: 18 }}
                transition={{ type: "spring", stiffness: 500, damping: 28, delay: open ? i * 0.06 : 0 }}
            >
                {link}
            </motion.div>
        );
    };

    return (
        <>
            {/* Desktop: always-visible vertical bar (left in Arabic, right in English) */}
            <nav
                aria-label={t.footer.contactTitle}
                data-testid="social-bar"
                className="fixed end-3 top-1/2 z-40 hidden -translate-y-1/2 flex-col gap-2.5 lg:flex"
            >
                {links.map((link) => renderLink(link))}
            </nav>

            {/* Mobile & tablet: floating toggle that fans the icons out */}
            <div
                className="fixed end-3 top-1/2 z-40 hidden -translate-y-1/2 flex-col items-center gap-2.5 md:flex lg:hidden"
                onPointerDown={() => setTick((v) => v + 1)}
            >
                <AnimatePresence>{open && links.map((link, i) => renderLink(link, i))}</AnimatePresence>
                <button
                    type="button"
                    aria-expanded={open}
                    aria-label={t.footer.contactTitle}
                    data-testid="social-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="flex h-11 w-11 items-center justify-center rounded-full bg-gold-500 text-ink shadow-[0_14px_30px_-12px_rgba(178,143,55,0.6)] transition-[background-color,transform] duration-300 hover:bg-gold-600 active:scale-90"
                >
                    <motion.span
                        animate={reduce ? {} : { rotate: open ? 90 : 0 }}
                        transition={{ duration: 0.25 }}
                        className="flex"
                    >
                        {open ? <IconClose className="h-5 w-5" /> : <Share2 className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />}
                    </motion.span>
                </button>
            </div>
        </>
    );
};

export default SocialBar;
